import { lazy, Suspense, useEffect } from "react";
import { Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "sileo";
import Layout from "./components/Layout";
import { HudLoader } from "@/components/SpotlightCard";

// -- Dashboard pages --
const Landing = lazy(() => import("./pages/Landing"));
const Overview = lazy(() => import("./pages/Overview"));
const Sources = lazy(() => import("./pages/Sources"));
const SourceDetail = lazy(() => import("./pages/SourceDetail"));
const Destinations = lazy(() => import("./pages/Destinations"));
const DestinationDetail = lazy(() => import("./pages/DestinationDetail"));
const Connections = lazy(() => import("./pages/Connections"));
const ConnectionDetail = lazy(() => import("./pages/ConnectionDetail"));
const Events = lazy(() => import("./pages/Events"));
const EventDetail = lazy(() => import("./pages/EventDetail"));
const Applications = lazy(() => import("./pages/Applications"));
const ApplicationDetail = lazy(() => import("./pages/ApplicationDetail"));
const EventTypes = lazy(() => import("./pages/EventTypes"));
const Portal = lazy(() => import("./pages/Portal"));
const Databases = lazy(() => import("./pages/Databases"));
const DatabaseDetail = lazy(() => import("./pages/DatabaseDetail"));
const Issues = lazy(() => import("./pages/Issues"));
const IssueDetail = lazy(() => import("./pages/IssueDetail"));
const Metrics = lazy(() => import("./pages/Metrics"));
const Topics = lazy(() => import("./pages/Topics"));
const TopicDetail = lazy(() => import("./pages/TopicDetail"));
const Replay = lazy(() => import("./pages/Replay"));
const Settings = lazy(() => import("./pages/Settings"));

// -- Auth pages --
const SignIn = lazy(() => import("./pages/SignIn"));
const SignUp = lazy(() => import("./pages/SignUp"));
const CliAuth = lazy(() => import("./pages/CliAuth"));

// -- Test pages --
const TestLanding = lazy(() => import("./pages/TestLanding"));
const TestInspector = lazy(() => import("./pages/TestInspector"));

// -- SEO / marketing pages --
const ContentPage = lazy(() => import("./pages/seo/ContentPage"));
const FeaturesHubRoute = lazy(() => import("./pages/seo/FeaturesHubRoute"));
const UseCasesHubRoute = lazy(() => import("./pages/seo/UseCasesHubRoute"));
const IntegrationsHubRoute = lazy(() => import("./pages/seo/IntegrationsHubRoute"));
const Pricing = lazy(() => import("./pages/seo/Pricing"));
const SitemapPage = lazy(() => import("./pages/seo/SitemapPage"));
const Privacy = lazy(() => import("./pages/seo/Privacy"));
const Terms = lazy(() => import("./pages/seo/Terms"));
const Changelog = lazy(() => import("./pages/Changelog"));

// -- Developer tool pages --
const ToolsHub = lazy(() => import("./pages/tools/ToolsHub"));
const HmacCalculator = lazy(() => import("./pages/tools/HmacCalculator"));
const JwtDecoder = lazy(() => import("./pages/tools/JwtDecoder"));
const EncoderDecoder = lazy(() => import("./pages/tools/EncoderDecoder"));
const CronBuilder = lazy(() => import("./pages/tools/CronBuilder"));
const SignatureValidator = lazy(() => import("./pages/tools/SignatureValidator"));
const PayloadTransformer = lazy(() => import("./pages/tools/PayloadTransformer"));
const TypeGenerator = lazy(() => import("./pages/tools/TypeGenerator"));
const WebhookDiff = lazy(() => import("./pages/tools/WebhookDiff"));
const WebhookToCode = lazy(() => import("./pages/tools/WebhookToCode"));
const CloudEventsConverter = lazy(() => import("./pages/tools/CloudEventsConverter"));
const WebhookRelay = lazy(() => import("./pages/tools/WebhookRelay"));
const MockApi = lazy(() => import("./pages/tools/MockApi"));
const EndpointHealth = lazy(() => import("./pages/tools/EndpointHealth"));
const WebhookScheduler = lazy(() => import("./pages/tools/WebhookScheduler"));
const IdempotencyTester = lazy(() => import("./pages/tools/IdempotencyTester"));

const TOOL_ROUTES = [
  ["/tools/hmac-calculator", HmacCalculator],
  ["/tools/jwt-decoder", JwtDecoder],
  ["/tools/encoder-decoder", EncoderDecoder],
  ["/tools/cron-builder", CronBuilder],
  ["/tools/signature-validator", SignatureValidator],
  ["/tools/payload-transformer", PayloadTransformer],
  ["/tools/type-generator", TypeGenerator],
  ["/tools/webhook-diff", WebhookDiff],
  ["/tools/webhook-to-code", WebhookToCode],
  ["/tools/cloudevents-converter", CloudEventsConverter],
  ["/tools/webhook-relay", WebhookRelay],
  ["/tools/mock-api", MockApi],
  ["/tools/endpoint-health", EndpointHealth],
  ["/tools/webhook-scheduler", WebhookScheduler],
  ["/tools/idempotency-tester", IdempotencyTester],
] as const;

const ConnectionWizard = lazy(() => import("./pages/ConnectionWizard"));

const FINGERPRINTIQ_PUBLIC_KEY =
  "fiq_live_0deb2a2ba20febe8f168e5985ed0bab3aa117df9ff11acbdeb3b95ebce5ae940";

let fingerprintClient: {
  identify: (options?: { tag?: unknown; timeout?: number }) => Promise<unknown>;
} | null = null;

async function identifyPage(path: string): Promise<void> {
  const { default: FingerprintIQ } = await import("@fingerprintiq/js");
  fingerprintClient ??= new FingerprintIQ({
    apiKey: FINGERPRINTIQ_PUBLIC_KEY,
    detectWallets: false,
    timeout: 5_000,
  });

  await fingerprintClient.identify({
    tag: {
      app: "hookstream",
      surface: "web",
      path,
    },
    timeout: 5_000,
  });
}

function FingerprintIQTracker() {
  const location = useLocation();

  useEffect(() => {
    const path = `${location.pathname}${location.search}`;
    void identifyPage(path).catch(() => {
      // Analytics must never affect the app shell.
    });
  }, [location.pathname, location.search]);

  return null;
}

const DASHBOARD_ROUTES = [
  ["/dashboard", Overview],
  ["/sources", Sources],
  ["/sources/:id", SourceDetail],
  ["/destinations", Destinations],
  ["/destinations/:id", DestinationDetail],
  ["/connections/new", ConnectionWizard],
  ["/connections", Connections],
  ["/connections/:id", ConnectionDetail],
  ["/events", Events],
  ["/events/:id", EventDetail],
  ["/applications", Applications],
  ["/applications/:id", ApplicationDetail],
  ["/event-types", EventTypes],
  ["/databases", Databases],
  ["/databases/:id", DatabaseDetail],
  ["/issues", Issues],
  ["/issues/:id", IssueDetail],
  ["/topics", Topics],
  ["/topics/:id", TopicDetail],
  ["/replay", Replay],
  ["/metrics", Metrics],
  ["/settings", Settings],
] as const;

export default function App() {
  return (
    <Suspense
      fallback={
        <div className="flex min-h-screen items-center justify-center">
          <HudLoader />
        </div>
      }
    >
      <FingerprintIQTracker />
      <Toaster position="bottom-right" />
      <Routes>
        {/* Public landing page */}
        <Route path="/" element={<Landing />} />

        {/* Public auth routes */}
        <Route path="/sign-in" element={<SignIn />} />
        <Route path="/sign-up" element={<SignUp />} />
        <Route path="/cli-auth" element={<CliAuth />} />

        {/* Public test routes (no auth gate) */}
        <Route path="/test" element={<TestLanding />} />
        <Route path="/test/:id" element={<TestInspector />} />

        {/* Portal (public, portal-token auth) */}
        <Route path="/portal/:appId" element={<Portal />} />

        {/* Free developer tools (no auth gate) */}
        <Route path="/tools" element={<ToolsHub />} />
        {TOOL_ROUTES.map(([path, Component]) => (
          <Route key={path} path={path} element={<Component />} />
        ))}

        {/* SEO / marketing pages */}
        <Route path="/features" element={<FeaturesHubRoute />} />
        <Route path="/features/:slug" element={<ContentPage basePath="/features" />} />
        <Route path="/use-cases" element={<UseCasesHubRoute />} />
        <Route path="/use-cases/:slug" element={<ContentPage basePath="/use-cases" />} />
        <Route path="/integrations" element={<IntegrationsHubRoute />} />
        <Route path="/integrations/:slug" element={<ContentPage basePath="/integrations" />} />
        <Route path="/compare/:slug" element={<ContentPage basePath="/compare" />} />
        <Route path="/what-are-webhooks" element={<ContentPage basePath="/what-are-webhooks" />} />
        <Route path="/pricing" element={<Pricing />} />
        <Route path="/sitemap" element={<SitemapPage />} />
        <Route path="/privacy" element={<Privacy />} />
        <Route path="/terms" element={<Terms />} />
        <Route path="/changelog" element={<Changelog />} />

        {/* Authenticated dashboard routes */}
        <Route element={<Layout />}>
          {DASHBOARD_ROUTES.map(([path, Component]) => (
            <Route key={path} path={path} element={<Component />} />
          ))}
        </Route>

        {/* Catch-all */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Suspense>
  );
}
